<script setup lang="ts">
import TreatmentPlanDetail from '@/components/common/TreatmentPlanDetail.vue';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
    fetchTreatmentPlan,
    useStageTariffCodeRollover,
    useTariffCodesRollover
} from '@/hooks/use-treatment-plans';
import { useRolloverStore } from '@/store/use-rollover-store';
import { showError, showSuccess } from '@/utils/toast';
import PreviewResultsDialog from '@/views/tools/previews/PreviewResultsDialog.vue';
import {
    ArrowRight,
    ChevronLeft,
    ChevronRight,
    Eye,
    RepeatIcon,
    Search
} from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();

interface RolloverData {
  membership_number?: string
  option_code?: string
  scheme_code?: string
  dependant_code?: string
  auth_start_date?: string
  auth_end_date: string
  sunset: string
  to_year: string
  tariff_codes: string[]
}

// Form inputs
const status = ref('enrolled')
const programmePhaseId = ref('1')
const toYear = ref('2026')
const sunset = ref('2025')

// Search and pagination
const searchQuery = ref('')
const currentPage = ref(1)
const itemsPerPage = ref(15)

// Treatment plan dialog
const isDialogOpen = ref(false)
const selectedRow = ref<RolloverData | null>(null)
const fetchedTreatmentPlan = ref<any>(null)
const isFetchingTreatmentPlan = ref(false)
const treatmentPlanError = ref<string | null>(null)

// Open treatment plan dialog
const openTreatmentPlanDialog = async (item: RolloverData) => {
  selectedRow.value = item
  isDialogOpen.value = true
  isFetchingTreatmentPlan.value = true
  treatmentPlanError.value = null
  fetchedTreatmentPlan.value = null
  
  try {
    const payload = {
      scheme_code: item?.scheme_code,
      membership_number: item?.membership_number,
      dependant_code: item?.dependant_code,
      option_code: item?.option_code,
      auth_start_date: item?.auth_start_date
    }
    
    // Fetch the data
    const tp = await fetchTreatmentPlan(payload)
    console.log(`Fetched Treatment Plan:`, tp)
    fetchedTreatmentPlan.value = tp
  } catch (err: any) {
    console.error('Error fetching treatment plan:', err)
    treatmentPlanError.value = err?.message || 'Failed to load treatment plan'
  } finally {
    isFetchingTreatmentPlan.value = false
  }
}

// Close treatment plan dialog
const closeTreatmentPlanDialog = () => {
  isDialogOpen.value = false
  selectedRow.value = null
  fetchedTreatmentPlan.value = null
  treatmentPlanError.value = null
}
// USE QUERY TO STAGE ROLLOVER
const { 
  data, 
  isLoading, 
  isError, 
  error, 
  refetch 
} = useStageTariffCodeRollover(
  status.value,
  programmePhaseId.value,
  toYear.value,
  sunset.value
)

const { mutate: rollover, isPending } = useTariffCodesRollover()

// Get store
const rolloverStore = useRolloverStore()

const emit = defineEmits<{
  'update:open': [value: boolean]
}>()

// Destructure state with storeToRefs
const { rolloverResults, rolloverDataList } = storeToRefs(rolloverStore)

// Filtered data based on search
const filteredData = computed(() => {
  if (!data.value) return []
  
  const query = searchQuery.value.toLowerCase().trim()
  if (!query) return data.value
  
  return data.value.filter((item: RolloverData) => 
    item.scheme_code?.toLowerCase().includes(query) ||
    item.option_code?.toLowerCase().includes(query) ||
    item.membership_number?.toLowerCase().includes(query)
  )
})

// Pagination
const totalPages = computed(() => Math.ceil(filteredData.value.length / itemsPerPage.value))

const paginatedData = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return filteredData.value.slice(start, end)
})

const goToPage = (page: number) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

// Reset to page 1 when search changes
const handleSearch = () => {
  currentPage.value = 1
}

// Computed property for safe access
const hasResults = computed(() => rolloverResults.value !== null)
const selectedCount = computed(() => rolloverDataList.value.length)

const isResultDialogOpen = ref(false)

// Check if all visible items are selected
const isAllSelected = computed(() => {
  if (paginatedData.value.length === 0) return false
  return paginatedData.value.every(item => isSelected(item))
})

// Check if some (but not all) visible items are selected
const isIndeterminate = computed(() => {
  const selectedInPage = paginatedData.value.filter(item => isSelected(item)).length
  return selectedInPage > 0 && selectedInPage < paginatedData.value.length
})

// Check if item is selected
const isSelected = (item: RolloverData) => {
  return rolloverDataList.value.some(
    selected => selected.membership_number === item.membership_number
  )
}

// Toggle selection
const toggleSelection = (item: RolloverData) => {
  if (isSelected(item)) {
    rolloverStore.removeRolloverDataByMembership(item.membership_number!)
  } else {
    rolloverStore.setRolloverDataList(item)
  }
}

// Toggle all visible items
const toggleAll = () => {
  if (isAllSelected.value) {
    // Deselect all visible items
    paginatedData.value.forEach(item => {
      if (isSelected(item)) {
        rolloverStore.removeRolloverDataByMembership(item.membership_number!)
      }
    })
  } else {
    // Select all visible items
    paginatedData.value.forEach(item => {
      if (!isSelected(item)) {
        rolloverStore.setRolloverDataList(item)
      }
    })
  }
}

const handleClose = () => {
  emit('update:open', false)
  rolloverStore.clearRolloverDataList()
  rolloverStore.clearRolloverData()
}

// Bulk rollover selected items
const handleBulkRollover = () => {
  if (selectedCount.value === 0) {
    showError('Please select at least one item')
    return
  }
  
  rollover({
    sunset: '2025',
    toYear: '2026',
    // @ts-ignore [TODO]
    payload: rolloverDataList.value
  }, {
    onSuccess: (data) => {
      showSuccess("Rollover completed!");
      rolloverStore.setRolloverResults(data)
      handleClose()
      isResultDialogOpen.value = true
    },
    onError: (error: any) => {
      showError(error.message || 'An error occurred during rollover');
    }
  })
}

if (hasResults) {
  isResultDialogOpen.value = true
}

// Clear all selections
const clearAllSelections = () => {
  rolloverStore.clearRolloverDataList()
}

const toTransactions = () => {
  router.push("/transactions")
}

</script>

<template>
  <div class="p-8">
    <!-- Header -->
    <div class="flex items-center justify-between border-b border-gray-200 pb-6 mb-6">
      <div>
        <h2 class="text-2xl font-semibold text-gray-800">
          Rollover - Eligible Health Seekers 
          <span v-if="data?.length > 0" class="text-slate-500">({{ data?.length }})</span>
        </h2>
        <p v-if="selectedCount > 0" class="text-sm text-gray-600 mt-2">
          {{ selectedCount }} item(s) selected
        </p>
      </div>
      
      <div class="flex gap-3">
        <Button
          v-if="selectedCount > 0"
          size="sm"
          @click="clearAllSelections()" 
          variant="outline"
          :disabled="isPending"
          class="text-red-600 bg-red-50 hover:bg-red-600 hover:text-white"
        >
          CLEAR ALL ({{ selectedCount }})
        </Button>
        
        <Button
          size="sm"
          @click="handleBulkRollover()" 
          :disabled="selectedCount === 0 || isPending"
          class="bg-[#225f7a] text-white hover:bg-green-800"
        >
          BULK ROLLOVER ({{ selectedCount }})
          <ArrowRight class="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>

    <!-- Search Bar -->
    <div v-if="!isLoading && !isError && data?.length" class="mb-6">
      <div class="relative max-w-md">
        <Search class="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          v-model="searchQuery"
          @input="handleSearch"
          placeholder="Search by scheme, option, or membership number..."
          class="pl-10"
        />
      </div>
      <p v-if="searchQuery" class="text-sm text-gray-600 mt-2">
        Found {{ filteredData.length }} result(s)
      </p>
    </div>
 
    <!-- Loading State -->
    <div v-if="isLoading" class="flex flex-col items-center justify-center py-20">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mb-4"></div>
      <p class="text-lg text-gray-600 font-medium">Loading eligible health seekers...</p>
    </div>
    
    <!-- Error State -->
    <div v-else-if="isError" class="flex flex-col items-center justify-center py-20">
      <div class="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
        <h3 class="text-lg font-semibold text-red-900 mb-3">Error Loading Data</h3>
        <p class="text-red-700 text-sm mb-4">{{ error?.message }}</p>
        <Button 
          @click="refetch()" 
          variant="outline"
          class="w-full border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
        >
          <RepeatIcon class="mr-2 h-4 w-4" />
          Try Again
        </Button>
      </div>
    </div>
    
    <!-- Processing Overlay -->
    <div 
      v-if="isPending" 
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div class="bg-white rounded-lg p-8 max-w-md shadow-2xl">
        <div class="flex flex-col items-center">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mb-4"></div>
          <h3 class="text-xl font-semibold text-gray-900 mb-2">Processing Rollover</h3>
          <p class="text-gray-600 text-center mb-4">
            Please wait while we process {{ selectedCount }} health seeker(s)...
          </p>
        </div>
      </div>
    </div>

    <!-- Preview Results Dialog -->
    <PreviewResultsDialog
      v-if="hasResults && isResultDialogOpen && rolloverResults"
      v-model:open="isResultDialogOpen"
      :response="[rolloverResults]"
    />
    
    <!-- View Treatment Plan Dialog -->
  <Dialog :open="isDialogOpen" @update:open="closeTreatmentPlanDialog">
    <DialogContent class="w-[80vw] max-w-none sm:max-w-none bg-slate-100 overflow-y-auto max-h-[90vh]">
      <DialogHeader>
        <DialogTitle>Treatment Plan Details</DialogTitle>
        <DialogDescription>
          Transaction data as received from the health insurance scheme.
        </DialogDescription>
      </DialogHeader>
      
      <!-- Loading State -->
      <div v-if="isFetchingTreatmentPlan" class="flex items-center justify-center py-12">
        <div class="flex flex-col items-center">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600 mb-3"></div>
          <p class="text-gray-600">Loading treatment plan...</p>
        </div>
      </div>
      
      <!-- Error State -->
      <div v-else-if="treatmentPlanError" class="bg-red-50 border border-red-200 rounded-lg p-4">
        <p class="text-red-700 text-sm">{{ treatmentPlanError }}</p>
      </div>
      
      <!-- Content State -->
      <div v-else-if="selectedRow && fetchedTreatmentPlan" class="space-y-4">
        <!-- Treatment Plan Data -->
        <div class="mt-6 border-t pt-4">
          <p class="text-xs font-semibold text-gray-600 uppercase mb-3">Treatment Plan</p>
          <TreatmentPlanDetail :data="fetchedTreatmentPlan" />
        </div>
      </div>
      
      <!-- Empty State -->
      <div v-else class="flex items-center justify-center py-8">
        <p class="text-gray-600">No treatment plan data available</p>
      </div>
    </DialogContent>
  </Dialog>
    <!-- Table -->
    <div v-if="!isLoading && !isError && filteredData.length > 0" class="space-y-4">
      <div class="border rounded-lg overflow-hidden bg-white shadow-sm">
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-50 border-b">
              <tr>
                <th class="w-12 px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    :checked="isAllSelected"
                    :indeterminate.prop="isIndeterminate"
                    @change="toggleAll"
                    :disabled="isPending"
                    class="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Scheme
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Option
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Membership Number
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Names
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Surname
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Dependant
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Phase
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Auth Period
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Rollover
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Tariff Codes
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  View
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr
                v-for="(item, index) in paginatedData"
                :key="index"
                :class="[
                  'hover:bg-gray-50 transition-colors',
                  isSelected(item) ? 'bg-green-50' : ''
                ]"
              >
                <td class="px-4 py-3">
                  <input
                    type="checkbox"
                    :checked="isSelected(item)"
                    @change="toggleSelection(item)"
                    :disabled="isPending"
                    class="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                </td>
                <td class="px-4 py-3">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-200 text-[#255f7a]">
                    {{ item?.scheme_code }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <span class="text-sm text-gray-900 font-medium">{{ item?.option_code }}</span>
                  <small class="block text-gray-500">{{ item?.option_name }}</small>
                </td>
                <td class="px-4 py-3">
                  <span class="text-sm font-mono text-gray-700">{{ item?.membership_number }}</span>
                </td>
                <td class="px-4 py-3">
                  <span class="text-sm font-mono text-gray-700">
                    {{ item?.health_seeker?.first_name }} {{ item?.health_seeker?.middle_name }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <span class="text-sm font-mono text-gray-700">{{ item?.health_seeker?.surname }}</span>
                </td>
                <td class="px-4 py-3">
                  <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                    {{ item.dependant_code }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                    {{ item?.programme_phase }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <div class="text-sm">
                    <div class="text-gray-900">{{ item?.auth_start_date }}</div>
                    <div class="text-gray-500 text-xs">to {{ item?.auth_end_date }}</div>
                  </div>
                </td>
                <td class="px-4 py-3">
                  <div class="flex items-center gap-1 text-sm text-gray-700">
                    <span>{{ item?.sunset }}</span>
                    <span class="text-gray-400">→</span>
                    <span>{{ item?.to_year }}</span>
                  </div>
                </td>
                <td class="px-4 py-3">
                  <div class="flex flex-wrap gap-1">
                    <span
                      v-for="code in item?.tariff_codes"
                      :key="code"
                      :class="[
                        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                        isSelected(item) 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-slate-200 text-slate-900'
                      ]"
                    >
                      {{ code }}
                    </span>
                  </div>
                </td>
                <td class="px-4 py-3">
                  <button
                    @click="openTreatmentPlanDialog(item)"
                    class="inline-flex items-center justify-center gap-1 py-1 px-2 border border-sky-900  text-[#225f7a] hover:bg-slate-200 rounded-lg transition-colors"
                    title="View Treatment Plan"
                  >
                    <Eye class="h-5 w-5 tshaks" /> Treatment
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination -->
      <div class="flex items-center justify-between px-4 py-3 bg-white border rounded-lg">
        <div class="text-sm text-gray-700">
          Showing {{ ((currentPage - 1) * itemsPerPage) + 1 }} to 
          {{ Math.min(currentPage * itemsPerPage, filteredData.length) }} of 
          {{ filteredData.length }} results
        </div>
        
        <div class="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            @click="goToPage(currentPage - 1)"
            :disabled="currentPage === 1"
          >
            <ChevronLeft class="h-4 w-4" />
            Previous
          </Button>
          
          <div class="flex gap-1">
            <Button
              v-for="page in totalPages"
              :key="page"
              v-show="page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1"
              size="sm"
              :variant="page === currentPage ? 'default' : 'outline'"
              @click="goToPage(page)"
              class="min-w-[40px]"
            >
              {{ page }}
            </Button>
          </div>
          
          <Button
            size="sm"
            variant="outline"
            @click="goToPage(currentPage + 1)"
            :disabled="currentPage === totalPages"
          >
            Next
            <ChevronRight class="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!isLoading && !isError && filteredData.length === 0" class="flex flex-col items-center justify-center py-20">
      <svg class="h-24 w-24 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
      </svg>
      <h3 class="text-lg font-semibold text-gray-700 mb-2">
        {{ searchQuery ? 'No Results Found' : 'No Health Seekers Found' }}
      </h3>
      <p class="text-gray-500 text-sm mb-4">
        {{ searchQuery 
          ? `No health seekers match "${searchQuery}"` 
          : 'There are no eligible health seekers for rollover at this time.' 
        }}
      </p>
      <Button 
        v-if="searchQuery"
        @click="searchQuery = ''"
        variant="outline"
      >
        Clear Search
      </Button>
      <Button 
        v-else
        @click="refetch()" 
        variant="outline"
      >
        <RepeatIcon class="mr-2 h-4 w-4" />
        Refresh
      </Button>
    </div>
  </div>
</template>
<style scoped>
</style>